// Home (brainstorms/2026-10-27-dashboard-for-all-users.md): the page every internal team
// member lands on. Four cards, the same for everyone, filled by role: what needs you, today,
// your numbers, and one card only your role needs. Replaced the Executive Dashboard.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiRefreshCw, FiAlertCircle, FiChevronRight } from 'react-icons/fi';
import { homeAPI } from '../services/api';
import { Dot } from './FoundationStep2';
import { Trend, fmt } from './MyAnalytics';
import './Home.css';

const REFRESH_MS = 5 * 60 * 1000;
const SHOW_FIRST = 10;
const CHIP = {
  BILLING: 'Billing', VISA: 'Visa', DOCS: 'Docs', LEAD: 'Lead', FEEDBACK: 'Feedback',
  TASK: 'Task', DUTY: 'Duty', PLAN: 'Plan', MEETING: 'Meeting',
  STUDENT: 'Student', JOB: 'Job seeker', TOURIST: 'Tourist', MEDICAL: 'Medical',
};
const BUCKET_LABEL = { overdue: 'Overdue', today: 'Today', week: 'This week' };
const PERIODS = [['month', 'Month'], ['quarter', 'Quarter'], ['year', 'Year']];

const ago = (iso, now) => {
  const mins = Math.max(0, Math.round((now - new Date(iso)) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} days ago`;
};
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const amounts = (rows) => (rows && rows.length
  ? rows.map((r) => `${r.currency} ${Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`).join(' · ')
  : '—');

const Card = ({ title, badge, action, error, className = '', children }) => (
  <section className={`home-card ${className}`}>
    <header className="home-card-head">
      <h3>{title}{badge ? <span className="home-badge">{badge}</span> : null}</h3>
      {action}
    </header>
    {error ? <p className="home-error"><FiAlertCircle aria-hidden="true" /> {error}</p> : children}
  </section>
);

const Chip = ({ kind }) => <span className="home-chip" data-kind={kind}>{CHIP[kind] || kind}</span>;

// ── Needs you today ─────────────────────────────────────────────────────────
const NeedsYou = ({ data, error }) => {
  const [all, setAll] = useState(false);
  const items = data?.items || [];
  const shown = all ? items : items.slice(0, SHOW_FIRST);
  const overdue = data?.counts?.overdue || 0;
  return (
    <Card title="Needs you today" className="home-needs" error={error}
          badge={overdue ? `${overdue} overdue` : null}>
      {data?.failed_sources?.length ? (
        <p className="home-muted">Some items couldn't load: {data.failed_sources.join(', ')}.</p>
      ) : null}
      {!items.length ? <p className="home-empty">Nothing waiting on you. Good work.</p> : (
        <ul className="home-list">
          {shown.map((i, n) => (
            <React.Fragment key={i.id}>
              {(n === 0 || shown[n - 1].bucket !== i.bucket) && (
                <li className="home-bucket" data-bucket={i.bucket}>{BUCKET_LABEL[i.bucket]}</li>
              )}
              <li>
                <Link to={i.link} className="home-row">
                  <Chip kind={i.kind} />
                  <span className="home-row-main">
                    <span className="home-row-title">{i.title}</span>
                    <span className="home-row-sub">{i.sub}{i.reason ? ` · ${i.reason}` : ''}</span>
                  </span>
                  <span className="home-row-due" data-bucket={i.bucket}>{shortDate(i.due)}</span>
                  <FiChevronRight aria-hidden="true" className="home-row-go" />
                </Link>
              </li>
            </React.Fragment>
          ))}
        </ul>
      )}
      {items.length > SHOW_FIRST && (
        <button type="button" className="home-link-btn" onClick={() => setAll(!all)}>
          {all ? 'Show fewer' : `Show all (${items.length})`}
        </button>
      )}
    </Card>
  );
};

// ── Today ───────────────────────────────────────────────────────────────────
const Agenda = ({ rows, error }) => (
  <Card title="Today" className="home-today" error={error}>
    {!rows?.length ? <p className="home-empty">No meetings, appointments or tasks due today.</p> : (
      <ul className="home-list">
        {rows.map((r, n) => (
          <li key={n}>
            <Link to={r.link} className="home-row">
              <span className="home-time">{r.time || 'All day'}</span>
              <span className="home-row-main">
                <span className="home-row-title">{r.title}</span>
                <span className="home-row-sub">{r.sub}</span>
              </span>
              <Chip kind={r.kind} />
            </Link>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

// ── My numbers ──────────────────────────────────────────────────────────────
const delta = (k) => {
  if (k.value == null || k.previous_value == null) return null;
  const d = Number(k.value) - Number(k.previous_value);
  if (!d) return <span className="home-delta">no change</span>;
  // Colour by better/worse, not up/down: a falling "processing time" is good news.
  const better = (d > 0) === (k.direction !== 'LOWER');
  return <span className="home-delta" data-better={better}>{d > 0 ? '▲' : '▼'} {fmt(Math.abs(d), k.unit)}</span>;
};

const Numbers = ({ data, error, period, onPeriod, loading }) => {
  const toggle = (
    <div className="home-toggle" role="group" aria-label="Period">
      {PERIODS.map(([key, label]) => (
        <button key={key} type="button" aria-pressed={period === key} disabled={loading}
                onClick={() => onPeriod(key)}>{label}</button>
      ))}
    </div>
  );
  const tiles = data?.tiles || [];
  return (
    <Card title="My numbers" className="home-numbers" error={error} action={toggle}>
      {data?.state === 'no_role' ? (
        <p className="home-empty">Your role isn't linked yet. Ask the COO.</p>
      ) : !tiles.length ? <p className="home-empty">No KPIs for your role yet.</p> : (
        <div className="home-tiles">
          {tiles.map((k, n) => (
            <div key={k.id} className={`home-tile${n === 0 ? ' home-hero' : ''}`} data-colour={k.colour || 'GREY'}
                 title={k.how_measured}>
              <div className="home-tile-head"><span>{k.name}</span><Dot colour={k.colour} /></div>
              <div className="home-tile-value">{fmt(k.value, k.unit)}</div>
              <div className="home-muted">
                {delta(k) || 'vs last month: —'}
                {k.colour === 'GREY' || !k.colour ? ' · no target yet' : ` · target ${k.target_text}`}
              </div>
              <Trend points={k.trend || []} unit={k.unit} />
            </div>
          ))}
        </div>
      )}
      <Link to="/my-analytics" className="home-link-btn">See all my numbers <FiChevronRight aria-hidden="true" /></Link>
    </Card>
  );
};

// ── Role panel ──────────────────────────────────────────────────────────────
const Stat = ({ label, value, to }) => {
  const body = <><span className="home-stat-value">{value}</span><span className="home-stat-label">{label}</span></>;
  return to ? <Link to={to} className="home-stat">{body}</Link> : <div className="home-stat">{body}</div>;
};
const Bars = ({ rows, valueKey = 'count', labelKey = 'label' }) => {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return (
    <ul className="home-bars">
      {rows.map((r, n) => (
        <li key={n}>
          <span className="home-bar-label">{r[labelKey]}</span>
          <span className="home-bar-track"><span className="home-bar" style={{ width: `${(r[valueKey] / max) * 100}%` }} /></span>
          <span className="home-bar-value">{r[valueKey]}</span>
        </li>
      ))}
    </ul>
  );
};
const Empty = ({ rows, children, text = 'Nothing yet.' }) => (rows && rows.length ? children : <p className="home-muted">{text}</p>);

const PANELS = {
  CHAIRMAN: (p) => (
    <>
      <div className="home-stats">
        {p.verticals.map((v) => <Stat key={v.kind} label={`Active ${v.label.toLowerCase()}`} value={v.count} />)}
      </div>
      <div className="home-split">
        <div><h4>Cash received this month</h4><p className="home-big">{amounts(p.cash_this_month)}</p>
          <p className="home-muted">Last month: {amounts(p.cash_last_month)}</p></div>
        <div><h4>Waiting on you</h4>
          {p.awaiting.map((a) => <Link key={a.label} to={a.link} className="home-line">{a.label}<b>{a.count}</b></Link>)}</div>
      </div>
    </>
  ),
  CEO: (p) => (
    <div className="home-funnels">
      {p.funnels.map((f) => (
        <div key={f.kind} className="home-funnel" data-kind={f.kind}>
          <h4>{f.label} <span className="home-muted">{f.total} total · {f.conversion_rate}% converted</span></h4>
          <Bars rows={f.steps.filter((s) => s.count)} />
        </div>
      ))}
    </div>
  ),
  COO: (p) => (
    <>
      <div className="home-stats">
        {p.visa_by_stage.map((s) => <Stat key={s.status} label={`Visa cases: ${s.label.toLowerCase()}`} value={s.count} to="/visa-cases" />)}
        <Stat label="Visa files needing a check" value={p.visa_attention} to="/visa-cases" />
      </div>
      <div className="home-split">
        <div><h4>Uploads waiting for a check, by consultant</h4>
          <Empty rows={p.docs_by_consultant} text="None waiting."><Bars rows={p.docs_by_consultant} labelKey="name" /></Empty></div>
        <div><h4>Most overdue tasks</h4>
          <Empty rows={p.overdue_by_person} text="No overdue tasks."><Bars rows={p.overdue_by_person} labelKey="name" /></Empty></div>
      </div>
    </>
  ),
  CFO: (p) => (
    <>
      <div className="home-split">
        <div><h4>Outstanding invoices</h4><p className="home-big">{amounts(p.outstanding)}</p>
          {p.ageing.map((a) => <div key={a.label} className="home-line">{a.label}<b>{amounts(a.amounts)}</b></div>)}</div>
        <div><h4>Received this week</h4><p className="home-big">{amounts(p.received_this_week)}</p>
          <Link to="/billing" className="home-line">Quotations expiring within 7 days<b>{p.quotes_expiring}</b></Link></div>
      </div>
    </>
  ),
  CSM: (p) => (
    <>
      <div className="home-stats">
        {p.deals_by_stage.map((s) => <Stat key={s.stage} label={s.label} value={s.count} to="/deals" />)}
        <Stat label="Leads not contacted" value={p.leads_not_contacted} to="/app-leads" />
      </div>
      <h4>Won this month, by consultant</h4>
      <Empty rows={p.won_this_month} text="No deals won yet this month."><Bars rows={p.won_this_month} labelKey="name" /></Empty>
    </>
  ),
  CMO: (p) => (
    <>
      <div className="home-stats">
        <Stat label="Plan items done this week" value={`${p.plan.done} / ${p.plan.planned}`} to="/marketing" />
        {p.social.map((s) => <Stat key={s.platform} label={`${s.platform} followers`} value={s.followers ?? '—'} />)}
      </div>
      <h4>Leads by source this month</h4>
      <Empty rows={p.leads_by_source} text="No leads yet this month."><Bars rows={p.leads_by_source} /></Empty>
    </>
  ),
  ADMIN: (p) => (
    <>
      <div className="home-stats">
        <Stat label="Open support tickets" value={p.open_tickets} to="/support-inbox" />
        {p.new_this_week.map((v) => <Stat key={v.kind} label={`New ${v.label.toLowerCase()} this week`} value={v.count} />)}
      </div>
      <h4>Latest app logins</h4>
      <Empty rows={p.recent_logins} text="No logins yet.">
        {p.recent_logins.map((l) => <div key={l.ref} className="home-line">{l.name} <span className="home-muted">{l.ref}</span><b>{ago(l.at, Date.now())}</b></div>)}
      </Empty>
    </>
  ),
  PERMANENT_EMPLOYEE: (p) => (
    <>
      <div className="home-stats">
        {p.verticals.map((v) => (
          <Stat key={v.kind} label={v.label} value={v.by_status.reduce((n, s) => n + s.count, 0)} />
        ))}
      </div>
      <h4>No contact in 14+ days</h4>
      <Empty rows={p.no_contact_14_days} text="Everyone has heard from you recently.">
        {p.no_contact_14_days.map((c) => (
          <Link key={`${c.kind}-${c.id}`} to={c.link} className="home-line"><Chip kind={c.kind} />{c.name}
            <b>{c.last_contacted_at ? ago(c.last_contacted_at, Date.now()) : 'never'}</b></Link>
        ))}
      </Empty>
    </>
  ),
  SUPPORT: (p) => (
    <div className="home-stats">
      <Stat label="Open tickets" value={p.open_tickets} to="/support-inbox" />
      {p.oldest && <Stat label={`Oldest: ${p.oldest.name}`} value={ago(p.oldest.waiting_since, Date.now())} to="/support-inbox" />}
    </div>
  ),
};

const RolePanel = ({ panel, error }) => {
  if (!panel && !error) return null;
  const render = panel && PANELS[panel.role];
  return (
    <Card title={panel?.title || 'Your role'} className="home-panel" error={error}>
      {render ? render(panel) : null}
    </Card>
  );
};

// While someone stands in for a role, Home shows that role's panel too (stand-in brainstorm Q1).
const CoveringPanels = ({ covers, error }) => {
  if (error) return <Card title="Covering" className="home-panel home-covering" error={error} />;
  return (covers || []).map((c) => {
    const render = PANELS[c.panel?.role];
    return (
      <Card key={c.role} title={`Covering: ${c.role_label} · until ${shortDate(c.ends_on)}`} className="home-panel home-covering">
        <p className="home-muted home-covering-sub">{c.panel?.title}. Its reminders are in Needs you.</p>
        {render ? render(c.panel) : null}
      </Card>
    );
  });
};

// ── Page ────────────────────────────────────────────────────────────────────
export default function Home() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [now, setNow] = useState(Date.now());
  const periodRef = useRef(period);

  const load = useCallback(async (p = periodRef.current) => {
    setLoading(true);
    try {
      const res = await homeAPI.get(p);
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.status === 403 ? 'Home is for the internal team.' : "Couldn't load Home. Try again.");
    } finally {
      setLoading(false);
      setNow(Date.now());
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = setInterval(() => load(), REFRESH_MS);
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [load]);

  const changePeriod = (p) => { periodRef.current = p; setPeriod(p); load(p); };
  const errors = data?.errors || {};
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="home-page">
      <header className="home-header">
        <div>
          <h2>Welcome back, <span>{data?.person?.first_name || ''}</span></h2>
          <p className="home-muted">
            {today}
            {data?.generated_at ? ` · Updated ${ago(data.generated_at, now)}` : ''}
          </p>
        </div>
        <button type="button" className="home-refresh" onClick={() => load()} disabled={loading} aria-label="Refresh">
          <FiRefreshCw aria-hidden="true" className={loading ? 'home-spin' : ''} /> Refresh
        </button>
      </header>

      {error && <p className="home-error"><FiAlertCircle aria-hidden="true" /> {error}</p>}
      {!data && loading && <p className="home-muted">Loading…</p>}

      {data && (
        <div className="home-grid">
          <NeedsYou data={data.needs_you} error={errors.needs_you} />
          <Agenda rows={data.agenda} error={errors.agenda} />
          <Numbers data={data.numbers} error={errors.numbers} period={period} onPeriod={changePeriod} loading={loading} />
          <RolePanel panel={data.panel} error={errors.panel} />
          <CoveringPanels covers={data.covering} error={errors.covering} />
        </div>
      )}
    </div>
  );
}
